import React, { useRef, useState } from 'react';
import { CameraIcon, CheckCircle2Icon, QrCodeIcon, Share2Icon } from 'lucide-react';
import { Avatar } from '../ui/Avatar';
import { Badge } from '../ui/Badge';
import { ProfileCard } from './ProfileCard';
import { ProfileIcon } from '../../utils/profileIcons';
import { useTerminology } from '../../hooks/useTerminology';
import { useAuth } from '../../contexts/AuthContext';
import { useAccount } from '../../contexts/AccountContext';
import { useVerification } from '../../contexts/VerificationContext';
const MAX_BYTES = 2 * 1024 * 1024;
const ACCEPTED = ['image/png', 'image/jpeg', 'image/webp'];

/**
 * The profile hero: who this is, established as plainly as possible.
 *
 * Every value comes from the authenticated identity or the account record. A
 * field the person has not filled in is absent or offered as an action — nothing
 * here is a demonstration value, and the verification badge reflects what was
 * actually established rather than being decoration beside a name.
 */
export function ProfileHero({
  onEdit
}: {
  onEdit: (fieldId: string) => void;
}) {
  const {
    term,
    phrase
  } = useTerminology();
  const {
    identity,
    updateProfile
  } = useAuth();
  const {
    account,
    institutionName,
    campusName
  } = useAccount();
  const {
    trust
  } = useVerification();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  if (!identity) return null;
  const name = identity.displayName || account.fullName;
  const joined = identity.createdAt ? new Date(identity.createdAt).toLocaleDateString('en-NG', {
    month: 'long',
    year: 'numeric'
  }) : null;
  const roleLabel = phrase(identity.activeRole === 'student' ? '{student}' : identity.activeRole.replace(/_/g, ' '));
  const choosePhoto = () => {
    setUploadError(null);
    fileRef.current?.click();
  };
  const onFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    // Refused for a stated reason rather than failing silently. SVG is not
    // accepted at all: it is a script container, not simply an image.
    if (!ACCEPTED.includes(file.type)) {
      setUploadError('That file type is not accepted. Use a PNG, JPEG or WebP image.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setUploadError('That image is larger than 2 MB. Choose a smaller one.');
      return;
    }
    setUploading(true);
    const reader = new FileReader();
    reader.onerror = () => {
      setUploading(false);
      setUploadError('That image could not be read. Try another one.');
    };
    reader.onload = () => {
      setUploading(false);
      updateProfile({
        photoUrl: String(reader.result)
      });
    };
    reader.readAsDataURL(file);
  };
  const academicLine = [institutionName, account.academic.faculty ? `Faculty of ${account.academic.faculty}` : null].filter(Boolean).join(' · ');
  const programmeLine = [account.academic.programme, account.academic.level].filter(Boolean).join(' · ');
  return <ProfileCard className="overflow-hidden" data-id="element-4457" data-mp="2ir48bdsran6x">
      <div className="grid gap-6 p-5 sm:p-6 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_260px]" data-id="element-4458" data-mp="2dqoxotzuztsn">
        {/* Identity */}
        <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-start" data-id="element-4459" data-mp="29olax97qp2s2">
          <div className="relative" data-id="element-4460" data-mp="24r1rzw03k1ts">
            <Avatar src={identity.photoUrl} initials={identity.photoInitials || account.initials} name={name} size="xl" badge={trust.currentStudent ? <span className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-surface bg-ok-700" aria-hidden data-id="element-4462" data-mp="1q1xbcj8ya5ns" /> : undefined} data-id="element-4461" data-mp="24s4ocjg6uwh3" />
            <button type="button" onClick={choosePhoto} className="absolute -bottom-1 -right-1 flex h-11 w-11 items-center justify-center rounded-full border border-line bg-surface text-ink-muted shadow-panel transition-colors duration-150 ease-out hover:text-ink" aria-label={identity.photoUrl ? 'Change profile photo' : 'Add a profile photo'} data-id="element-4463" data-mp="1h1ha7gfojam3">
              <CameraIcon className="h-4 w-4" data-id="element-4464" data-mp="3cadz33x59oh1" />
            </button>
            <input ref={fileRef} type="file" accept={ACCEPTED.join(',')} className="sr-only" onChange={onFile} tabIndex={-1} data-id="element-4465" data-mp="2xeg6fvmfj29k" />
          </div>

          <div className="min-w-0" data-id="element-4466" data-mp="2ogw3mi8x4xwu">
            <div className="flex flex-wrap items-center gap-2" data-id="element-4467" data-mp="2td4gfozn8f0u">
              <h2 className="text-h2 font-semibold tracking-tight text-ink" data-id="element-4468" data-mp="2tfh48is31jlt">{name}</h2>
              {trust.academicIdentityVerified && <CheckCircle2Icon className="h-5 w-5 text-brand-700" aria-label="Academic identity verified" data-id="element-4469" data-mp="rmj7wcz3k8w4" />}
            </div>
            <p className="cz-num mt-0.5 text-body text-ink-muted" data-id="element-4470" data-mp="wrwfze57p5o9">@{identity.username}</p>

            <div className="mt-3 flex flex-wrap items-center gap-2" data-id="element-4471" data-mp="1aj1l2vrcmq5z">
              <Badge tone="brand" plain data-id="element-4472" data-mp="1akcouvksnxx3">
                {roleLabel}
              </Badge>
              {trust.currentStudent ? <Badge tone="ok" data-id="element-4473" data-mp="1kg64woxq29jm">Verified</Badge> : <Badge tone="neutral" data-id="element-4474" data-mp="1kiikwwjx69u1">Not yet verified</Badge>}
            </div>

            <div className="mt-3 space-y-0.5 text-body-sm text-ink-muted" data-id="element-4475" data-mp="1fjyy6txi4u0m">
              {academicLine ? <p data-id="element-4476" data-mp="1pfsjb1padvu0">{academicLine}</p> : <button type="button" onClick={() => onEdit('institution')} className="font-medium text-brand-700 hover:underline" data-id="element-4477" data-mp="1pgactqkkdvy8">
                  {phrase('{institution} not added')} →
                </button>}
              {programmeLine ? <p data-id="element-4478" data-mp="1u8voiuq0ofxy">{programmeLine}</p> : <p className="text-ink-subtle" data-id="element-4479" data-mp="1z6ducc8qmf1q">Programme not added</p>}
            </div>

            {uploading && <p className="mt-2 text-body-sm text-ink-muted" data-id="element-4480" data-mp="243dyghm117q7">Adding your photo…</p>}
            {uploadError && <p className="mt-2 text-body-sm text-danger-700" role="alert" data-id="element-4481" data-mp="291fgr0bmp30m">
                {uploadError}
              </p>}
          </div>
        </div>

        {/* Bio, location, joined */}
        <div className="min-w-0 space-y-3 border-line lg:border-l lg:pl-6" data-id="element-4482" data-mp="2i23xw3wbjjhq">
          {identity.bio ? <p className="max-w-[46ch] text-body text-ink" data-id="element-4483" data-mp="2ru9vbt5cat4b">{identity.bio}</p> : <button type="button" onClick={() => onEdit('bio')} className="text-body font-medium text-brand-700 hover:underline" data-id="element-4484" data-mp="2wtcrsp797r0o">
              Add a short bio →
            </button>}
          <dl className="space-y-1.5 text-body-sm text-ink-muted" data-id="element-4485" data-mp="p8e0g5hrpgsc">
            <div className="flex items-center gap-2" data-id="element-4486" data-mp="l69bvl66fack">
              <ProfileIcon name="location" className="h-4 w-4 shrink-0 text-ink-subtle" data-id="element-4487" data-mp="l4pt9nx1sn7m" />
              <dt className="sr-only" data-id="element-4488" data-mp="g7g489dkmdkb">Location</dt>
              <dd data-id="element-4489" data-mp="b83bj800qc9t">
                {identity.location ?? <button type="button" onClick={() => onEdit('location')} className="font-medium text-brand-700 hover:underline" data-id="element-4490" data-mp="b9xvaizjzycb">
                    Add your location
                  </button>}
              </dd>
            </div>
            {joined && <div className="flex items-center gap-2" data-id="element-4491" data-mp="u7h69s5knr1d">
                <ProfileIcon name="events" className="h-4 w-4 shrink-0 text-ink-subtle" data-id="element-4492" data-mp="u4uypsiolbj5" />
                <dt className="sr-only" data-id="element-4493" data-mp="1ro66xiiscn96">Joined</dt>
                <dd data-id="element-4494" data-mp="1mv36qrji6fk0">Joined {joined}</dd>
              </div>}
            <div className="flex items-center gap-2" data-id="element-4495" data-mp="v81wb8cvmtc7">
              <ProfileIcon name="campus" className="h-4 w-4 shrink-0 text-ink-subtle" data-id="element-4496" data-mp="v68q1u66eb4q" />
              <dt className="sr-only" data-id="element-4497" data-mp="zza4y26duaob">{term('campus')}</dt>
              <dd data-id="element-4498" data-mp="m7dioereauzu">{campusName}</dd>
            </div>
          </dl>
        </div>

        {/* Sharing */}
        <div className="space-y-2.5" data-id="element-4499" data-mp="q875f4t3q3r5">
          <ShareTile icon={<Share2Icon className="h-[18px] w-[18px]" data-id="element-4501" data-mp="q6m6z43o20o0" />} title="Share profile" detail={`campuzon.ng/@${identity.username}`} onClick={() => {
          const url = `${window.location.origin}/community/people/@${identity.username}`;
          if (navigator.clipboard) void navigator.clipboard.writeText(url);
        }} data-id="element-4500" data-mp="q98h9bhxyu9x" />
          <ShareTile icon={<QrCodeIcon className="h-[18px] w-[18px]" data-id="element-4503" data-mp="19yiy4stfbl9m" />} title="My QR code" detail="Show your code to connect" to={`/community/people/@${identity.username}`} data-id="element-4502" data-mp="19v4u7g543402" />
        </div>
      </div>
    </ProfileCard>;
}
function ShareTile({
  icon,
  title,
  detail,
  onClick,
  to
}: {
  icon: React.ReactNode;
  title: string;
  detail: string;
  onClick?: () => void;
  to?: string;
}) {
  const content = <>
      <span aria-hidden className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-800" data-id="element-4504" data-mp="3w319a5gaup6v">
        {icon}
      </span>
      <span className="min-w-0 text-left" data-id="element-4505" data-mp="33evltaxv2m3c">
        <span className="block text-body font-medium text-ink" data-id="element-4506" data-mp="33fyftrqhd4nh">{title}</span>
        <span className="cz-num block truncate text-body-sm text-ink-muted" data-id="element-4507" data-mp="2yhdajdwdfus6">{detail}</span>
      </span>
    </>;
  const classes = 'flex min-h-touch-lg w-full items-center gap-3 rounded-xl border border-line bg-canvas px-4 py-3 transition-colors duration-150 ease-out hover:border-line-strong';
  if (to) {
    return <a href={to} className={classes} data-id="element-4508" data-mp="2kn2udkyfx8xq">
        {content}
      </a>;
  }
  return <button type="button" onClick={onClick} className={classes} data-id="element-4509" data-mp="2fpb2us1pjou2">
      {content}
    </button>;
}

/** Kept beside the hero so a loading profile has the same shape as a loaded one. */
export function ProfileHeroSkeleton() {
  return <ProfileCard className="p-6" data-id="element-4510" data-mp="fo27gjasn7w3">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center" data-id="element-4511" data-mp="aq0qq50ubkns">
        <div className="cz-skeleton h-28 w-28 rounded-full bg-canvas" data-id="element-4512" data-mp="5rqt7nrn87z0" />
        <div className="flex-1 space-y-2.5" data-id="element-4513" data-mp="5ttkeb4j875b">
          <div className="cz-skeleton h-7 w-56 rounded bg-canvas" data-id="element-4514" data-mp="1qndr2dicdyb" />
          <div className="cz-skeleton h-4 w-32 rounded bg-canvas" data-id="element-4515" data-mp="1rymzhpq2dwt" />
          <div className="cz-skeleton h-4 w-72 rounded bg-canvas" data-id="element-4516" data-mp="3t38fhw6xyem2" />
        </div>
      </div>
    </ProfileCard>;
}